
import React, { useState, useEffect } from 'react';
import ImageUpload from '../../../sharedcomponents/imageupload/imageupload';
import { fetchApi } from '../../../services/api';
import { buildD6Payload } from '../../../services/payloads/d6Payload';
import { getEightDInfoId } from '../../../services/eightDInfoStore';
import { getCurrentUserEmail } from '../../../services/auth';
import '../d1/d1.css';
import '../d2/d2.css';
import '../d6/d6.css';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import TwoMonthDatePicker from '../../../sharedcomponents/two-month-datepicker/TwoMonthDatePicker';

// -----------------------------
// Helpers
// -----------------------------
const toIsoOrNull = (date) => {
  if (!date) return null;
  try {
    const d = date instanceof Date ? date : new Date(date);
    return isNaN(d.getTime()) ? null : d.toISOString();
  } catch {
    return null;
  }
};

const D6 = ({ expanded, onSetClick, handleCloseForm, onToggleAccordion, isEnabled = false, onSaved, eightDInfoId, currentUserEmail = getCurrentUserEmail() || sessionStorage.getItem('userEmail') || '', teamMembers = [], d6Data, d6ResponsibleData, mode, readOnly = false }) => {
  // When readOnly is true, disable all edit interactions
  const effectiveEnabled = isEnabled && !readOnly;
  const canView = isEnabled;
  // Header states
  const [responsiblePerson, setResponsiblePerson] = useState('');
  const [plannedDate, setPlannedDate] = useState(null);
  const [achievedDate, setAchievedDate] = useState(null);
  const [errors, setErrors] = useState({ person: '', planned: '', achieved: '' });

  // D6: Permanent solution field
  const [permanentSolution, setPermanentSolution] = useState('');

  // Attachments & removal flags
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [attachmentsToRemove, setAttachmentsToRemove] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);

  // Save state & notices
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveNotice, setSaveNotice] = useState(null);
  const [d6Id, setD6Id] = useState(null);
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [formSaved, setFormSaved] = useState(false);
  const [isResponsibleSet, setIsResponsibleSet] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAchievedDate, setShowAchievedDate] = useState(false);
  const [formClosed, setFormClosed] = useState(true);

  // Determine if current user is the responsible person for this form
  const isCurrentUserResponsible = responsiblePerson && currentUserEmail && 
    responsiblePerson.toLowerCase() === currentUserEmail.toLowerCase();
  // Block form when user is not originator (readOnly) and not the responsible person
  const isFormBlocked = readOnly ? !isCurrentUserResponsible : false;

  // Check if responsible person is already set (only when d6Id exists, meaning Set was clicked)
  useEffect(() => {
    if (d6Id) {
      setIsResponsibleSet(true);
    }
  }, [d6Id]);

  // Fetch D6 data on mount if eightDInfoId exists
  useEffect(() => {
    // Fetch from API if eightDInfoId exists and no props data
    if (eightDInfoId && !d6Data && !d6ResponsibleData && mode === 'edit') {
      const fetchD6Data = async () => {
        try {
          
          const response = await fetchApi('/api/D6/GetD6', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ EightDInfoId: Number(eightDInfoId) }),
          });
          
          
          if (response?.data) {
            const d6 = response.data;
            
            if (d6.D6Id || d6.d6Id) { setD6Id(d6.D6Id || d6.d6Id); setFormSaved(true); }
            if (d6.PermanentSolution) setPermanentSolution(d6.PermanentSolution);
            if (d6.ResponsiblePerson) setResponsiblePerson(d6.ResponsiblePerson);
            if (d6.PlannedDate) setPlannedDate(new Date(d6.PlannedDate));
            if (d6.AchievedDate) setAchievedDate(new Date(d6.AchievedDate));
            
            if (d6.Attachments && Array.isArray(d6.Attachments)) {
              setUploadedFiles(d6.Attachments);
            }
          }
        } catch (error) {
        }
      };
      fetchD6Data();
      return;
    }
    
    // Handle props data
    if (d6Data) {
      const data = Array.isArray(d6Data) ? d6Data[0] : d6Data;
      if (data) {
        if (data.D6Id || data.d6Id) { setD6Id(data.D6Id || data.d6Id); setFormSaved(true); }
        if (data.PermanentSolution) setPermanentSolution(data.PermanentSolution);
        if (data.ResponsiblePerson) setResponsiblePerson(data.ResponsiblePerson);
        if (data.PlannedDate) setPlannedDate(new Date(data.PlannedDate));
        if (data.AchievedDate) setAchievedDate(new Date(data.AchievedDate));
        
        if (data.Attachments && Array.isArray(data.Attachments)) {
          setUploadedFiles(data.Attachments);
        }
      }
    }
    
    if (d6ResponsibleData) {
      const respData = Array.isArray(d6ResponsibleData) ? d6ResponsibleData[0] : d6ResponsibleData;
      if (respData) {
        setResponsiblePerson(respData.ResponsiblePerson || '');
        setPlannedDate(respData.PlannedDate ? new Date(respData.PlannedDate) : null);
        setAchievedDate(respData.AchievedDate ? new Date(respData.AchievedDate) : null);
      }
    }
  }, [eightDInfoId, d6Data, d6ResponsibleData]);

  const handlePermanentSolutionChange = (e) => {
    setPermanentSolution(e.target.value);
    setIsFormTouched(true);
  };

  const validateField = (field, value) => {
    let error = '';
    if (field === 'person' && !value) error = 'Responsible person is required';
    if (field === 'planned' && !value) error = 'Planned date is required';
    if (field === 'achieved' && !value) error = 'Achieved date is required';
    setErrors((prev) => ({ ...prev, [field]: error }));
  };
  const handlePersonChange = (value) => {
    setResponsiblePerson(value);
    validateField('person', value);
  };
  const handlePlannedChange = (value) => {
    setPlannedDate(value);
    validateField('planned', value);
  };
  const handleAchievedChange = (value) => {
    setAchievedDate(value);
    validateField('achieved', value);
  };


  const handleUpdateResponsiblePerson = async () => {
    if (!responsiblePerson) return;
    
    const currentEightDInfoId = eightDInfoId || getEightDInfoId();
    setSaveError(null);
    setSaveNotice(null);

    if (!currentEightDInfoId) {
      setSaveError('eightDInfoId is missing.');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        D6Id: d6Id || undefined,
        EightDInfoId: currentEightDInfoId,
        ResponsiblePerson: responsiblePerson,
        PlannedDate: plannedDate?.toISOString(),
        AchievedDate: achievedDate?.toISOString(),
        ModifiedBy: currentUserEmail
      };

      const endpoint = d6Id ? '/api/D6/UpdateD6Responsible' : '/api/D6/CreateD6';
      const response = await fetchApi(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response?.data?.D6Id || response?.data?.d6Id) {
        setD6Id(response.data.D6Id || response.data.d6Id);
      }

      setIsResponsibleSet(true);
      setFormClosed(false);
      setSaveNotice(d6Id ? "Updated D6 form successfully." : "D6 responsible person set successfully.");
      setTimeout(() => setSaveNotice(null), 3000);
    } catch (e) {
      setSaveError(e?.message ?? 'Failed to set responsible person.');
      setTimeout(() => setSaveError(null), 2000);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const currentEightDInfoId = eightDInfoId || getEightDInfoId();
    
    setSaveError(null);
    setSaveNotice(null);
    setSaving(true);
    
    try {
      const payload = buildD6Payload({
        eightDInfoId: currentEightDInfoId,
        permanentSolution,
        responsiblePerson,
        plannedDate,
        achievedDate,
        uploadedFiles,
        isDisabled,
        attachmentsToRemove,
        currentUserEmail,
        d6Id,
      });

      
      const endpoint = d6Id ? '/api/D6/UpdateD6' : '/api/D6/CreateD6';
      const data = await fetchApi(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      if (data?.data?.D6Id) {
        if (data?.data?.D6Id || data?.data?.d6Id) setD6Id(data.data.D6Id || data.data.d6Id);
      }

      // Re-fetch to get attachment IDs
      try {
        const refreshed = await fetchApi('/api/D6/GetD6', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ EightDInfoId: Number(currentEightDInfoId) }),
        });
        if (refreshed?.data?.Attachments) setUploadedFiles(refreshed.data.Attachments);
      } catch (e) { }

      setAttachmentsToRemove([]);
      setSaveNotice('D6 permanent solution saved successfully.');
      setTimeout(() => setSaveNotice(null), 2000);
      setFormSaved(true);
      
      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      setSaveError(error?.message || 'Failed to save D6 permanent solution.');
      setTimeout(() => setSaveError(null), 2000);
    } finally {
      setSaving(false);
    }
  };

  const toggleAccordion = () => {
    if (readOnly && isEnabled) {
      onToggleAccordion();
      return;
    }
    if (effectiveEnabled && isEditMode && (d6Id || (responsiblePerson && plannedDate))) {
      onToggleAccordion();
    }
  };

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <>
      <Box className={`d3-header ${canView ? 'enabled' : 'disabled'}`} onClick={toggleAccordion} style={{ cursor: canView ? 'pointer' : 'not-allowed', pointerEvents: canView ? 'auto' : 'none', opacity: canView ? 1 : 0.5 }}>
        {/* Left Section */}
        <Box className="d3-left-section">
          <Box className="d3-circle" style={achievedDate && formSaved ? { backgroundColor: '#4ccb91', borderColor: '#4ccb91' } : (responsiblePerson || isEditMode || formSaved ? { backgroundColor: '#f8c239', borderColor: '#f8c239' } : {})} />
          <span className="d3-title">D6</span>
          <span className="d3-subtitle">Validate Permanent CA/Solution</span> {/* text from your file */}
          {/* Tooltip */}
          <Tooltip
            title={
              <span>
                Implement containment to control the situation and mitigate further negative effects of the
                problem. (segregation, selection, temporary actions in the process etcâ€¦..)
              </span>
            }
            arrow
            placement="bottom-start"
            componentsProps={{
              tooltip: { className: 'custom-tooltip' },
              arrow: { className: 'custom-arrow' }
            }}
          >
            <IconButton>
              <InfoOutlinedIcon sx={{ color: '#666' }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Responsible Person */}
        <Box className="responsible-box" onClick={(e) => e.stopPropagation()}>
          <Select
            value={responsiblePerson}
            onChange={(e) => handlePersonChange(e.target.value)}
            displayEmpty
            variant="standard"
            className="responsible-select"
            error={!!errors.person}
            disabled={!effectiveEnabled}
            sx={{
              '& .MuiInput-underline:after': {
                borderBottomColor: '#800040',
              },
              '& .MuiInput-underline:before': {
                borderBottomColor: '#ccc',
              },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                borderBottomColor: '#800040',
              }
            }}
          >
            <MenuItem value="" sx={{ display: 'none' }}>Responsible Person</MenuItem>
            {responsiblePerson && !teamMembers.some(m => (m.EmailId || m.emailId) === responsiblePerson) && (
              <MenuItem value={responsiblePerson}>{responsiblePerson}</MenuItem>
            )}
            {teamMembers.map((member) => (
              <MenuItem key={member.D1Id} value={member.EmailId || member.emailId}>
                {member.TeamMemberName || member.teamMemberName}
              </MenuItem>
            ))}
          </Select>
          {errors.person && <Typography className="error-text">{errors.person}</Typography>}
        </Box>

        {/* Planned Date */}
        <Box onClick={(e) => e.stopPropagation()}>
          <TwoMonthDatePicker
            value={plannedDate}
            onChange={(value) => handlePlannedChange(value)}
            label="Planned Date"
            disabled={!effectiveEnabled || (isResponsibleSet && !isEditMode)}
            InputLabelProps={{ style: { fontSize: '0.8rem', color: '#777' } }}
          />
        </Box>

        {/* Achieved Date - enabled when responsible person is set */}
        <Box onClick={(e) => {
          e.stopPropagation();
          if (isResponsibleSet && !expanded) {
            setIsEditMode(true);
            setFormClosed(false);
            setIsFormTouched(false);
            onToggleAccordion();
          }
        }}>
          <TwoMonthDatePicker
            value={achievedDate}
            onChange={(d) => {
              setAchievedDate(d);
              setIsFormTouched(true);
              if (!isEditMode) {
                setIsEditMode(true);
                onToggleAccordion();
              }
            }}
            label="Achieved Date"
            disabled={!isResponsibleSet}
            maxDate={new Date()}
            InputLabelProps={{ style: { fontSize: '0.8rem', color: isEditMode && !achievedDate ? '#d32f2f' : '#777' } }}
            sx={isEditMode && !achievedDate ? {
              '& .MuiInput-underline:before': { borderBottomColor: '#d32f2f' },
              '& .MuiInput-underline:after': { borderBottomColor: '#d32f2f' },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#d32f2f' },
              '& .MuiInputLabel-root': { color: '#d32f2f !important' },
            } : {}}
          />
        </Box>

        {/* Edit/Update Buttons */}
        {isResponsibleSet ? (
          <Box className="filBtn float-left" onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined" 
              disabled={!effectiveEnabled}
              onClick={() => { setIsEditMode(true); setFormClosed(false); onToggleAccordion(); }}
              sx={{ minWidth: '60px', fontSize: '0.75rem' }}
            >
              Edit
            </Button>
            <Button 
              variant="outlined" 
              disabled={!effectiveEnabled || !responsiblePerson || !plannedDate} 
              onClick={handleUpdateResponsiblePerson}
              sx={{ minWidth: '70px', fontSize: '0.75rem' }}
            >
              Update
            </Button>
          </Box>
        ) : (
          <Box className="filBtn float-left" onClick={(e) => e.stopPropagation()}>
            <Button variant="outlined" disabled={!effectiveEnabled || !responsiblePerson || !plannedDate} onClick={handleUpdateResponsiblePerson}>Set</Button>
          </Box>
        )}
      </Box>

      {/* Success/Error Messages */}
      {(saveNotice || saveError) && (
        <Box sx={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, minWidth: '300px' }}>
          {saveError && <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>}
          {saveNotice && <Alert severity="success" onClose={() => setSaveNotice(null)}>{saveNotice}</Alert>}
        </Box>
      )}

      {/* Accordion Form */}
      {expanded && (isEditMode || readOnly) && (
        <Accordion expanded={expanded} sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box className="accordion-close-button-container">
              <Button
                className="close-button"
                variant="contained"
                color="secondary"
                startIcon={<CloseIcon />}
                onClick={() => { setIsEditMode(false); setFormClosed(true); handleCloseForm(); }}
              >
                Close Form
              </Button>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            {/* Q1: Permanent Solution */}
            <Box className="question-block">
              <Box className="question-label">
                <span className="d2-qnum">Q1</span>
                <span className="d2-question-text">Permanent Solution Statement?</span>
              </Box>

              <FormControl fullWidth variant="standard" sx={{ mt: 1 }}>
                <InputLabel shrink htmlFor="permanent-solution"></InputLabel>
                <TextField
                  className="customTextarea"
                  id="permanent-solution"
                  name="permanentSolution"
                  value={permanentSolution}
                  onChange={handlePermanentSolutionChange}
                  onFocus={() => setIsFormTouched(true)}
                  multiline
                  variant="standard"
                  InputProps={{ disableUnderline: false }}
                  fullWidth
                  sx={{
                    '& .MuiInput-underline:after': {
                      borderBottomColor: '#800040',
                    },
                    '& .MuiInput-underline:before': {
                      borderBottomColor: '#ccc',
                    },
                    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                      borderBottomColor: '#800040',
                    }
                  }}
                />
              </FormControl>
            </Box>

            {/* Attachments */}
            <ImageUpload onFilesSelected={(files) => {
              uploadedFiles.forEach(ef => {
                const efId = ef.AttachmentId || ef.attachmentId;
                if (efId && !files.some(f => (f.AttachmentId || f.attachmentId) === efId)) {
                  setAttachmentsToRemove(prev => prev.includes(efId) ? prev : [...prev, efId]);
                }
              });
              setUploadedFiles(files);
              setIsFormTouched(true);
            }} folderPath="D6" existingFiles={uploadedFiles} maxFiles={3} rejectedExtensions={['doc', 'docx']} />

            {/* Save Button */}
            <Box className="save-btn-container">
              <Button 
                variant="contained" 
                className="save-btn" 
                onClick={handleSave}
                disabled={saving || !isFormTouched}
                sx={{
                  backgroundColor: isFormTouched ? '#800040' : '#ccc',
                  color: '#fff',
                  borderRadius: '20px',
                  textTransform: 'none',
                  px: 4,
                  cursor: isFormTouched ? 'pointer' : 'not-allowed'
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>

            {/* Save notices */}
            {saveError && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error">{saveError}</Alert>
              </Box>
            )}
            {saveNotice && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="success">{saveNotice}</Alert>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};

export default D6;

